/**
 * Recipes API Client
 * Frontend API calls for managing recipes
 */

import { apiCall } from '../api';
import type {
  Recipe,
  RecipeFilters,
  CreateRecipeRequest,
  CopyRecipeRequest,
  UpdateRecipeRequest,
  RecipeResponse,
  RecipesResponse,
} from '@/types/api';

/**
 * Fetch recipes with optional filtering and pagination
 */
export async function fetchRecipes(
  filters: RecipeFilters = {}
): Promise<RecipesResponse> {
  const params = new URLSearchParams();

  if (filters.page) params.set('page', filters.page.toString());
  if (filters.pageSize) params.set('pageSize', filters.pageSize.toString());
  if (filters.search) params.set('search', filters.search);
  if (filters.category) params.set('category', filters.category);
  if (filters.activeOnly === false) params.set('activeOnly', 'false');
  if (filters.includeIngredientCount) params.set('includeIngredientCount', 'true');

  const queryString = params.toString();
  const url = queryString ? `/api/recipes?${queryString}` : '/api/recipes';

  return apiCall<RecipesResponse>(url, {
    method: 'GET',
  });
}

/**
 * Fetch a single recipe by ID
 */
export async function fetchRecipe(id: number): Promise<Recipe> {
  const response = await apiCall<RecipeResponse>(`/api/recipes/${id}`, {
    method: 'GET',
  });
  return response.recipe;
}

/**
 * Create a new recipe (Admin/Caterer only)
 */
export async function createRecipe(
  data: CreateRecipeRequest
): Promise<Recipe> {
  const response = await apiCall<RecipeResponse>('/api/recipes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.recipe;
}

/**
 * Copy an existing recipe and its ingredient links (Admin/Caterer only)
 */
export async function copyRecipe(
  id: number,
  data: CopyRecipeRequest
): Promise<Recipe> {
  const response = await apiCall<RecipeResponse>(`/api/recipes/${id}/copy`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.recipe;
}

/**
 * Update an existing recipe (Admin/Caterer only)
 */
export async function updateRecipe(
  id: number,
  data: UpdateRecipeRequest
): Promise<Recipe> {
  const response = await apiCall<RecipeResponse>(`/api/recipes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.recipe;
}

/**
 * Delete a recipe (Admin/Caterer only)
 * Note: This is a soft delete (sets isActive = false)
 */
export async function deleteRecipe(id: number): Promise<void> {
  await apiCall<void>(`/api/recipes/${id}`, {
    method: 'DELETE',
  });
}
